import { useMemo, useState, useCallback, useContext } from 'react'
import { getItineraryCountries } from 'checkout/lib/utils/cruises/itinerary'
import { max, min, nonNullable } from 'lib/array/arrayUtils'
import { countOccupants, countTravellersAsOccupants } from 'lib/offer/occupancyUtils'
import { isLEHotel, isCruiseOffer } from 'lib/offer/offerTypes'
import { getUpsellDismissalStorageKey } from 'lib/order/upsellUtils'
import {
  get as getLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import useInsuranceQuote from 'hooks/Insurance/useInsuranceQuote'
import useInsuranceProducts from 'hooks/Insurance/useInsuranceProducts'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectInsurancePriceAdjustmentPercentage } from 'lib/insurance/insurancePriceAdjustmentExperiment'
import useInsurancePriceExperiment from 'checkout/hooks/useInsurancePriceExperiment'
import GeoContext from 'contexts/geoContext'
import useInsuranceUpsellCommonData from './useInsuranceUpsellCommonData'
import useCheapestInsuranceQuotePrice from './useCheapestInsuranceQuotePrice'
import type { InsuranceUpsellData } from './insuranceUpsellTypes'

export type { InsuranceUpsellData } from './insuranceUpsellTypes'

function useInsuranceUpsell(
  order: App.Order,
): {
  insuranceUpsellEnabled: true
  insuranceUpsell: App.InsuranceUpsell
  handleInsuranceUpsellDismiss: () => void
  insuranceDataLoading: boolean
} | {
  insuranceUpsellEnabled: false
  insuranceUpsell: undefined
  handleInsuranceUpsellDismiss: undefined
  insuranceDataLoading: boolean
} {
  const [dismissed, setDismissed] = useState<boolean>(!!getLocalStorage(getUpsellDismissalStorageKey('insurance', order.id)))

  const {
    luxplus: { fetchingLuxPlusSubscription, ableToViewLuxPlusBenefits, luxPlusDiscountedInsuranceEnabled },
    offers: { offers, fetching },
    datesAreValid,
    flags: { isInsuranceSupported, orderHasInsurance },
  } = useInsuranceUpsellCommonData(order)

  const handleInsuranceUpsellDismiss = useCallback(() => {
    setLocalStorage(getUpsellDismissalStorageKey('insurance', order.id), true)
    setDismissed(true)
  }, [order.id])

  const [earliestItem, latestItem] = useMemo(() => {
    const possibleUpsells: Array<InsuranceUpsellData> = [
      ...nonNullable(order.items.map(item => {
        const hotelOffer = offers.find(offer => offer.id === item.offer.id)
        if (isLEHotel(hotelOffer) && item.offer.property && item.reservation) {
          return {
            offerId: item.offer.id,
            occupants: {
              adults: item.reservation.adults,
              children: item.reservation.children,
              infants: item.reservation.infants,
            },
            destinationCountries: nonNullable([hotelOffer.property.geoData.country]),
            destinationCountryCode: hotelOffer.property.geoData.countryCode,
            reservationType: item.reservationType,
            startDate: item.reservation.startDate,
            endDate: item.reservation.endDate,
            status: item.status,
            type: 'hotel',
          }
        }
      })),
      ...nonNullable(order.bedbankItems.map(item => {
        const occupancies: Array<App.Occupants> = item.rooms.map(room => room.occupancy)
        const occupants = countOccupants(occupancies)

        return {
          offerId: item.offer.id,
          occupants: {
            adults: occupants?.adults,
            children: occupants?.children,
            infants: occupants?.infants,
          },
          destinationCountries: [item.offer.property.address.countryName],
          reservationType: item?.reservationId,
          startDate: item.checkIn.format('YYYY-MM-DD'),
          endDate: item.checkOut.format('YYYY-MM-DD'),
          status: item.status,
          type: 'bedbank_hotel',
        }
      })),
      ...nonNullable(order.tourItems.map(item => {
        const travellers = countTravellersAsOccupants(item.tour.travellers)

        return {
          offerId: item.tourId,
          occupants: {
            adults: travellers.adults,
            children: travellers.children,
            infants: travellers.infants,
          },
          destinationCountries: item.tour.countriesVisited,
          reservationType: item.reservationId,
          startDate: item.tour.startDate,
          endDate: item.tour.endDate,
          status: item.status,
          type: 'tour',
        }
      })),
      ...nonNullable(order.cruiseItems.map(item => {
        const cruiseOffer = offers.find(offer => offer.id === item.cruiseOfferId)

        const passengers = countTravellersAsOccupants(item.passengers)
        if (isCruiseOffer(cruiseOffer)) {
          return {
            offerId: item.cruiseOfferId,
            occupants: {
              adults: passengers.adults,
              children: passengers.children,
              infants: passengers.infants,
            },
            destinationCountries: nonNullable(getItineraryCountries(cruiseOffer)),
            reservationType: item.reservationId,
            startDate: item.departureDate,
            endDate: item.arrivalDate,
            status: item.status,
            type: 'cruise',
          }
        }
      })),
    ].filter(item => item.status === 'completed')

    const earliest = min(possibleUpsells, item => new Date(item.startDate))
    const latest = max(possibleUpsells, item => new Date(item.endDate))

    return [earliest, latest]
  }, [order, offers])

  const { currentRegionName } = useContext(GeoContext)
  const isDomestic = useMemo(() => !!earliestItem?.destinationCountries?.every(country => country == currentRegionName), [currentRegionName, earliestItem?.destinationCountries])
  const isCruise = earliestItem?.type === 'cruise'
  const [products] = useInsuranceProducts('travel-insurance', {
    isCruise,
    isDomestic,
    disabled: !datesAreValid,
  })
  // Post-purchase upsell is a fresh insurance decision by definition, so the price experiment arm
  // applies deterministically instead of reading incidental checkout-cart state.
  const priceAdjustmentPercentage = useAppSelector(selectInsurancePriceAdjustmentPercentage)
  const [quote, fetchingQuotes] = useInsuranceQuote(products[0]?.id,
    {
      startDate: earliestItem?.startDate,
      endDate: latestItem?.endDate,
      destinationCountries: earliestItem?.destinationCountries,
      coverAmount: order?.total,
      travellers: earliestItem?.occupants,
      isCruise,
      type: 'travel-insurance',
      priceAdjustmentPercentage,
    }, { disabled: !datesAreValid })

  const [cheapestQuotePrice, cheapestQuoteLuxPlusPrice] = useCheapestInsuranceQuotePrice(
    { ableToViewLuxPlusBenefits, luxPlusDiscountedInsuranceEnabled, quote, earliestItem, latestItem },
  )

  const enabled = !dismissed &&
    isInsuranceSupported &&
    !orderHasInsurance &&
    datesAreValid

  // Showing the upsell price is the activation point for the insurance price experiment.
  useInsurancePriceExperiment({ canActivate: enabled && !!quote, isFreshInsuranceDecision: true })

  const insuranceUpsell = useMemo<App.InsuranceUpsell>(() => {
    return {
      checkCanViewLuxPlusBenefits: ableToViewLuxPlusBenefits,
      isEnabledLuxPlusDiscountedInsurance: luxPlusDiscountedInsuranceEnabled,
      fetchingLuxPlusSubscription,
      fetchingQuotes,
      cheapestQuotePrice,
      cheapestQuoteLuxPlusPrice,
      orderId: order.id,
    }
  }, [
    ableToViewLuxPlusBenefits,
    cheapestQuoteLuxPlusPrice,
    cheapestQuotePrice,
    fetchingLuxPlusSubscription,
    fetchingQuotes,
    luxPlusDiscountedInsuranceEnabled,
    order.id,
  ])

  if (enabled) {
    return {
      insuranceUpsellEnabled: true,
      insuranceUpsell,
      handleInsuranceUpsellDismiss,
      insuranceDataLoading: fetching,
    }
  } else {
    return {
      insuranceUpsellEnabled: false,
      insuranceUpsell: undefined,
      handleInsuranceUpsellDismiss: undefined,
      insuranceDataLoading: fetching,
    }
  }
}

export default useInsuranceUpsell
